import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  type RefObject,
} from 'react';
import { css, useTheme } from '@emotion/react';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import {
  type RestModalFinalOffer,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { FunnelButton } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  ModalDesktopInnerSpacer,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type Props = {
  modalInfo: RestModalFinalOffer;
  handleAction: (action: RestModalForwards) => void;
};

export const FinalOffer: React.FC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      requires: {
        generic_actions = [],
        info: { title, details, image },
      },
    },
    handleAction,
  } = props;
  const { mobileView, tabletView, mobilePadding } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const equalButtonSize = theme.complianceEqualButtonSize;
  const offerContext = useContext(EditOfferPageContext);
  const isPreview = !!offerContext;
  const isMobilePreview =
    isPreview && offerContext?.previewDevice === PreviewDevice.MOBILE;
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );
  const btnRefs = useRef<RefObject<HTMLButtonElement>[]>([]);
  useEffect(() => {
    btnRefs.current.forEach((btn, idx) =>
      refsCtx?.addRef(`${refsPrefix}-btn${idx}`, btn)
    );
  }, [btnRefs?.current.length]);
  useEffect(() => {
    btnRefs.current = generic_actions.map(
      (x, i) => btnRefs.current[i] ?? createRef<HTMLButtonElement>()
    );
  }, [generic_actions.length]);

  return (
    <ModalDesktopInnerSpacer
      css={[
        mobilePadding,
        css`
          ${
            mobileView &&
            css`
              flex: 1 1 auto;
            `
          };
        `,
      ]}
      className={cs('final-offer', 'inner-spacer', 'display-grid', {
        desktop: tabletView,
        mobile: mobileView,
        hasImage: !!image,
      })}
    >
      <TitleTheme ref={titleRef} title={title} />

      <div css={{ marginBottom: '1rem' }} ref={imageRef}>
        {image && (
          <img
            alt=""
            src={image}
            css={css`
              width: 100%;
              height: 100%;
              max-height: 600px;
              object-fit: cover;
              object-position: center;
              border-radius: 4px;
            `}
          />
        )}
      </div>

      <div ref={descRef}>
        <CustomerHtml value={details} />
      </div>
      {/* Added padding to make total 27px in mobile for equal size buttons */}
      <div
        className={cs('display-grid action-items', {
          '!tw-flex tw-flex-col tw-justify-center tw-px-[11px]':
            equalButtonSize && (mobileView || isMobilePreview),
          '!tw-flex tw-justify-center':
            equalButtonSize && !mobileView && !isMobilePreview,
        })}
      >
        {generic_actions.map((ga, idx) => (
          <FunnelButton
            ref={btnRefs.current[idx]}
            transparent={idx > 0}
            key={idx}
            onClick={() => handleAction(ga)}
            classes="d-flex align-items-center justify-content-center"
          >
            {ga.icon && <ActionButtonIcons icon={ga.icon} />}
            <span style={{ paddingLeft: '5px' }}>{ga.text}</span>
          </FunnelButton>
        ))}
      </div>
    </ModalDesktopInnerSpacer>
  );
};
